import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import {
  ExceptionsClient,
  GlobalAdminsClient,
  UserConfigClient,
  UserConfigViewModel,
} from 'src/app/shared/models/autogenerated';
import {
  finishLoading,
  startLoading,
} from 'src/app/shared/store/shared.operators';
import { ToastMessagesService } from 'src/app/shared/services/toast-messages.service';
import {
  loadAllExceptionsAccess,
  loadAllGlobalAdminsAccess,
  loadHasUserGlobalAdminAccess,
  loadUserConfig,
  setAllExceptionsAccess,
  setAllGlobalAdminsAccess,
  setHasUserGlobalAdminAccess,
  setUserConfig,
  updateUserConfig,
} from './global-settings.actions';
import { selectUserConfigForm } from './global-settings.selectors';

@Injectable()
export class GlobalSettingsEffects {
  loadUserConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserConfig),
      startLoading(this.store),
      mergeMap(() =>
        this.userConfigService.getCurrentUserConfigOrDefault().pipe(
          map((userConfig) =>
            setUserConfig({
              userConfig,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  updateUserConfig$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserConfig),
        withLatestFrom(this.store.select(selectUserConfigForm)),
        mergeMap(([_, userConfigForm]) => {
          return this.userConfigService.updateCurrentUserConfig(
            userConfigForm.value as UserConfigViewModel
          );
        }),
        tap(() =>
          this.toastMessagesService.displaySuccess(
            `Success: Global Settings User Config saved`
          )
        )
      ),
    { dispatch: false }
  );

  loadHasUserGlobalAdminAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadHasUserGlobalAdminAccess),
      startLoading(this.store),
      mergeMap(() =>
        this.globalAdminService.getHasUserGlobalAdminAccess().pipe(
          map((hasUserGlobalAdminAccess) =>
            setHasUserGlobalAdminAccess({
              hasUserGlobalAdminAccess,
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  loadAllGlobalAdminsAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllGlobalAdminsAccess),
      startLoading(this.store),
      mergeMap(() =>
        this.globalAdminService.getGlobalAdmins().pipe(
          map((globalAdmins) =>
            setAllGlobalAdminsAccess({
              globalAdminsIds: globalAdmins.map((x) => x.oid),
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  loadAllExceptionsAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllExceptionsAccess),
      startLoading(this.store),
      mergeMap(() =>
        this.exceptionService.getExceptions().pipe(
          map((exceptions) =>
            setAllExceptionsAccess({
              exceptionsIds: exceptions.map((x) => x.oid),
            })
          )
        )
      ),
      finishLoading(this.store)
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private toastMessagesService: ToastMessagesService,
    private userConfigService: UserConfigClient,
    private globalAdminService: GlobalAdminsClient,
    private exceptionService: ExceptionsClient
  ) {}
}
